import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { Image } from '@/components/common/image';
import { Video } from '@/components/common/video';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TruncatedText } from './truncated-text';
// import 'github-markdown-css/github-markdown.css'; // 建议引入样式，解决标题和列表的间距

interface AddMaterialModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  title?: string;
  content: string;
  onOpenChange?: (open: boolean) => void;
  onSave?: (ids: string[]) => void;
}


/** 素材详情弹窗 */
export const MarkdownPreviewModal: React.FC<AddMaterialModalProps> = ({
  trigger,
  open: externalOpen,
  title,
  onOpenChange,
  onSave,
  content,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const processedContent = React.useMemo(() => {
    if (!content) return '';
    return content.replace(/\\./g, '.');
  }, [content]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[750px] max-h-[95%] min-h-[95%] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6 pb-4">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <TruncatedText text={title || 'Preview'} className='w-[600px]' />
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='markdown-body bg-white flex-1 p-6 pr-2 pt-0 select-none overflow-y-auto' data-theme="light">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ node, children, ...props }) => (
                <h1 className="text-2xl font-bold mb-4 text-[rgba(24,29,39,1)]" {...props}>
                  {children}
                </h1>
              ),
              h2: ({ node, children, ...props }) => (
                <h2 className="text-xl font-bold mb-4 text-[rgba(24,29,39,1)]" {...props}>
                  {children}
                </h2>
              ),
              h3: ({ node, children, ...props }) => (
                <h3 className="text-lg font-bold mb-4 text-[rgba(24,29,39,1)]" {...props}>
                  {children}
                </h3>
              ),
              ol: ({ node, children, ...props }) => (
                <ol className="list-decimal pl-6 mb-4 text-[rgba(24,29,39,1)] space-y-2" {...props}>
                  {children}
                </ol>
              ),
              ul: ({ node, children, ...props }) => (
                <ul className="list-disc pl-6 mb-4 text-[rgba(24,29,39,1)] space-y-2" {...props}>
                  {children}
                </ul>
              ),
              li: ({ node, children, ...props }) => (
                <li className="text-[rgba(24,29,39,1)] leading-relaxed" {...props}>
                  {children}
                </li>
              ),
              p: ({ node, children, ...props }) => (
                <p className="mb-4 text-[rgba(24,29,39,1)]" {...props}>
                  {children}
                </p>
              ),
              a: ({ node, href, children, ...props }) => {
                if (!href) return null;
                const cleanHref = decodeURIComponent(href).replace(/\\/g, '');
                return (
                  <a
                    {...props}
                    href={cleanHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#326bdc] underline cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {children}
                  </a>
                );
              },
              // 自定义图片/视频渲染逻辑
              img: ({ node, src, alt, ...props }) => {
                const isVideo = src?.match(/\.(mp4|webm|ogg|mov|MP4|MOV)$/i) || src?.includes('video');
                if (isVideo) {
                  return (
                    <Video
                      controls
                      // poster={`${src}?x-oss-process=video/snapshot,w_180,t_0,f_png,m_fast`}
                      style={{
                        width: 180,
                        borderRadius: '8px',
                        marginBottom: 8,
                        marginTop: 8
                      }}
                    >
                      <source src={src} type="video/mp4" />
                      {/* 您的浏览器不支持视频播放。 */}
                    </Video>
                  );
                }
                // 如果是普通图片，保持原样
                return (
                  <div className='flex justify-center items-center'>
                    <Image
                      src={src}
                      alt={alt}
                      style={{
                        maxWidth: '40%',
                        marginBottom: 8,
                        marginTop: 8
                      }}
                      {...props}
                    />
                  </div>
                );
              }
            }}
          >
            {processedContent}
          </ReactMarkdown>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
